import { useState, type KeyboardEvent } from 'react';
import { Box } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  AgronodButton,
  AgronodDialog,
  AgronodNumberField,
  AgronodTypography,
} from '@agronod/mui-components';

interface Props {
  open: boolean;
  /** Antal gårdar i populationen – övre gräns för antalet. */
  total: number;
  upptagen?: boolean;
  onBekrafta: (antal: number) => void;
}

/**
 * Antal-steget (kravspec v4 C.3) som dialog vid första besöket: antalet
 * gårdar som ska revideras anges och låses. Går inte att stänga – enda
 * vägen förbi är att ange antalet eller gå tillbaka till kundvalet.
 */
export const AntalDialog = ({ open, total, upptagen = false, onBekrafta }: Props) => {
  const [antal, setAntal] = useState<number | undefined>(undefined);
  const angivet = antal !== undefined;
  const giltigt = angivet && Number.isInteger(antal) && antal >= 1 && antal <= total;
  const bekrafta = () => giltigt && onBekrafta(antal);

  return (
    <AgronodDialog
      open={open}
      closable={false}
      title="Hur många gårdar ska revideras?"
      maxWidth="sm"
      fullWidth
      actions={
        <>
          <AgronodButton variant="text" component={Link} to="/">
            Till verifieringsuppdragen
          </AgronodButton>
          <AgronodButton
            variant="contained"
            disabled={!giltigt || upptagen}
            onClick={bekrafta}
          >
            Fortsätt
          </AgronodButton>
        </>
      }
    >
      <AgronodTypography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
        Ange hur många av de {total} gårdarna som ska väljas ut för revision
        på plats. Antalet styr urvalet och kan inte ändras här i efterhand.
      </AgronodTypography>
      <Box
        sx={{ maxWidth: 220 }}
        onKeyDown={(e: KeyboardEvent) => e.key === 'Enter' && bekrafta()}
      >
        <AgronodNumberField
          label="Antal gårdar"
          value={antal}
          onChange={setAntal}
          decimalScale={0}
          autoFocus
          error={angivet && !giltigt}
          helperText={
            angivet && !giltigt
              ? `Ange ett heltal mellan 1 och ${total}.`
              : `Mellan 1 och ${total}`
          }
        />
      </Box>
    </AgronodDialog>
  );
};
